import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import MyHouseNoticeSummaryResponse from '@Types/entities/MyHouseNoticeSummaryResponse'
import { landAxios } from '@Root/utils/landAxios'
import { MY_HOUSE_NOTICE_CATEGORY } from '@Root/contants/myhouse'
import MyHouseNoticeListResponse from '@Types/entities/MyHouseNoticeListResponse'
import MyhouseNoticeResponse from '@Types/entities/MyhouseNoticeResponse'
import { FETCH_STATUS } from '@Root/contants'
import FetchResponse from '@Types/inner/FetchResponse'

export interface NoticeListParam {
  complexNo: number
  category?: string
  offset?: number
  limit?: number
}

export interface NoticeParam {
  complexNo: number
  noticeSequence: number
}

export type MyhouseNoticeDetail = {
  fetchStatus: FETCH_STATUS
} & MyhouseNoticeResponse

export const fetchAttentionNoticeList = createAsyncThunk(
  'myhouse/fetchAttentionNotices',
  async (params: NoticeListParam) =>
    await (
      await landAxios.get(`/myhouse/complexes/${params.complexNo}/notices/attention`, {
        params: {
          ...params,
          complexNo: null,
        },
      })
    ).data,
)

export const fetchNoticeList = createAsyncThunk(
  'myhouse/fetchNotices',
  async (params: NoticeListParam) =>
    await (
      await landAxios.get(`/myhouse/complexes/${params.complexNo}/notices`, {
        params: {
          ...params,
          complexNo: null,
        },
      })
    ).data,
)

export const fetchInitialNoticeList = createAsyncThunk(
  'myhouse/fetchInitialNotices',
  async (params: NoticeListParam) =>
    await (
      await landAxios.get(`/myhouse/complexes/${params.complexNo}/notices`, {
        params: {
          ...params,
          complexNo: null,
        },
      })
    ).data,
)

export const fetchNotice = createAsyncThunk(
  'myhouse/fetchNotice',
  async (params: NoticeParam) => await (await landAxios.get(`/myhouse/complexes/${params.complexNo}/notices/${params.noticeSequence}`)).data,
)

export const fetchMyNotice = createAsyncThunk(
  'myhouse/fetchMyNotice',
  async (params: NoticeParam) => await (await landAxios.get(`/myhouse/complexes/${params.complexNo}/my/notices/${params.noticeSequence}`)).data,
)

export type MyHouseNoticeState = {
  attentionList: FetchResponse<
    {
      [key in MY_HOUSE_NOTICE_CATEGORY]: MyHouseNoticeSummaryResponse[]
    }
  >
  noticeList: {
    [key in MY_HOUSE_NOTICE_CATEGORY]: MyHouseNoticeSummaryResponse[]
  } & { fetchStatus: FETCH_STATUS }
  hasMore: {
    [key in MY_HOUSE_NOTICE_CATEGORY]: boolean
  }
  detail: MyhouseNoticeDetail
  currentCategory: MY_HOUSE_NOTICE_CATEGORY
  isShowTabLoading: boolean
}

export const initialState: MyHouseNoticeState = {
  attentionList: {
    [MY_HOUSE_NOTICE_CATEGORY.ALL]: [],
    [MY_HOUSE_NOTICE_CATEGORY.OFFICE]: [],
    [MY_HOUSE_NOTICE_CATEGORY.MEETING]: [],
    [MY_HOUSE_NOTICE_CATEGORY.TENANT]: [],
    [MY_HOUSE_NOTICE_CATEGORY.GOVERNMENT]: [],
    [MY_HOUSE_NOTICE_CATEGORY.ETC]: [],
    fetchStatus: FETCH_STATUS.INITIAL,
  },
  noticeList: {
    [MY_HOUSE_NOTICE_CATEGORY.ALL]: [],
    [MY_HOUSE_NOTICE_CATEGORY.OFFICE]: [],
    [MY_HOUSE_NOTICE_CATEGORY.MEETING]: [],
    [MY_HOUSE_NOTICE_CATEGORY.TENANT]: [],
    [MY_HOUSE_NOTICE_CATEGORY.GOVERNMENT]: [],
    [MY_HOUSE_NOTICE_CATEGORY.ETC]: [],
    fetchStatus: FETCH_STATUS.INITIAL,
  },
  hasMore: {
    [MY_HOUSE_NOTICE_CATEGORY.ALL]: true,
    [MY_HOUSE_NOTICE_CATEGORY.OFFICE]: true,
    [MY_HOUSE_NOTICE_CATEGORY.MEETING]: true,
    [MY_HOUSE_NOTICE_CATEGORY.TENANT]: true,
    [MY_HOUSE_NOTICE_CATEGORY.GOVERNMENT]: true,
    [MY_HOUSE_NOTICE_CATEGORY.ETC]: true,
  },
  detail: {
    fetchStatus: FETCH_STATUS.INITIAL,
  } as MyhouseNoticeDetail,
  currentCategory: MY_HOUSE_NOTICE_CATEGORY.ALL,
  isShowTabLoading: false,
} as MyHouseNoticeState

const myhouseNoticeSlice = createSlice({
  name: 'notice',
  initialState,
  reducers: {
    updateNoticeSequence: (state: MyHouseNoticeState, action: PayloadAction<number>) => {
      state.detail.sequence = action.payload
    },
    updateNoticeCategory: (state: MyHouseNoticeState, action: PayloadAction<MY_HOUSE_NOTICE_CATEGORY>) => {
      state.currentCategory = action.payload
      state.isShowTabLoading = true
    },
    updateNoticeDetail: (state: MyHouseNoticeState, action: PayloadAction<MyhouseNoticeDetail>) => {
      state.detail = action.payload
    },
  },
  extraReducers: {
    [fetchAttentionNoticeList.fulfilled.type]: (state: MyHouseNoticeState, action: PayloadAction<MyHouseNoticeListResponse>) => {
      const category = action.payload.category as MY_HOUSE_NOTICE_CATEGORY
      state.attentionList[category] = action.payload.noticeList
      state.attentionList.fetchStatus = FETCH_STATUS.SUCCESS
      state.isShowTabLoading = false
    },
    [fetchInitialNoticeList.fulfilled.type]: (state: MyHouseNoticeState, action: PayloadAction<MyHouseNoticeListResponse>) => {
      const category = action.payload.category as MY_HOUSE_NOTICE_CATEGORY
      state.noticeList.fetchStatus = FETCH_STATUS.SUCCESS
      state.noticeList[category] = action.payload.noticeList
      state.hasMore[category] = action.payload.hasMore
      state.isShowTabLoading = false
    },
    [fetchNoticeList.fulfilled.type]: (state: MyHouseNoticeState, action: PayloadAction<MyHouseNoticeListResponse>) => {
      const category = action.payload.category as MY_HOUSE_NOTICE_CATEGORY

      state.noticeList.fetchStatus = FETCH_STATUS.SUCCESS
      state.noticeList[category] = [...state.noticeList[category], ...action.payload.noticeList]
      state.hasMore[category] = action.payload.hasMore
      state.isShowTabLoading = false
    },
    [fetchNotice.fulfilled.type]: (state: MyHouseNoticeState, action: PayloadAction<MyhouseNoticeResponse>) => {
      state.detail = {
        ...action.payload,
        fetchStatus: FETCH_STATUS.SUCCESS,
      }
    },
    [fetchMyNotice.fulfilled.type]: (state: MyHouseNoticeState, action: PayloadAction<MyhouseNoticeResponse>) => {
      state.detail = {
        ...action.payload,
        fetchStatus: FETCH_STATUS.SUCCESS,
      }
    },

    [fetchAttentionNoticeList.pending.type]: (state: MyHouseNoticeState) => {
      state.attentionList.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchInitialNoticeList.pending.type]: (state: MyHouseNoticeState) => {
      state.noticeList.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchNoticeList.pending.type]: (state: MyHouseNoticeState) => {
      state.noticeList.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchNotice.pending.type]: (state: MyHouseNoticeState) => {
      state.noticeList.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchMyNotice.pending.type]: (state: MyHouseNoticeState) => {
      state.detail.fetchStatus = FETCH_STATUS.PENDING
    },

    [fetchAttentionNoticeList.rejected.type]: (state: MyHouseNoticeState) => {
      state.attentionList.fetchStatus = FETCH_STATUS.FAILURE
    },
    [fetchInitialNoticeList.rejected.type]: (state: MyHouseNoticeState) => {
      state.noticeList.fetchStatus = FETCH_STATUS.FAILURE
    },
    [fetchNoticeList.rejected.type]: (state: MyHouseNoticeState) => {
      state.noticeList.fetchStatus = FETCH_STATUS.FAILURE
    },
    [fetchNotice.rejected.type]: (state: MyHouseNoticeState) => {
      state.detail = {
        ...initialState.detail,
        fetchStatus: FETCH_STATUS.FAILURE,
      }
    },
    [fetchMyNotice.rejected.type]: (state: MyHouseNoticeState) => {
      state.detail = {
        ...initialState.detail,
        fetchStatus: FETCH_STATUS.FAILURE,
      }
    },
  },
})

export const { updateNoticeSequence, updateNoticeCategory, updateNoticeDetail } = myhouseNoticeSlice.actions
export default myhouseNoticeSlice.reducer
